import { useEffect, useMemo } from 'react'
import classNames from 'classnames'
import { AnimatePresence, motion } from 'framer-motion'
import { LinkButton } from '@/atoms/Button'
import { CaretRightIcon } from '@/atoms/Icons/CaretRightIcon'
import { CircleCheckIcon } from '@/atoms/Icons/CircleCheckIcon'
import { CloseIcon } from '@/atoms/Icons/CloseIcon'
import { PayItForwardOutlineIcon } from '@/atoms/Icons/PayItForwardOutlineIcon'
import { CaptionMD, Text } from '@/atoms/Text'
import { paths } from '@/constants/paths'
import { usePayItForwardOfferData } from '@/contexts/PayItForwardOffersDataContext'
import { formatPrice, getPriceForCurrency } from '@/services/PriceFormatter'
import { useTranslate } from '@/utils/translate/translate-client'
import { TheatricalExpressPriceOptions } from '@/views/PayItForward/TheatricalPayItForwardView/components/TheatricalPifExpressOffers/TheatricalExpressPriceOptions'
import { TheatricalPifCheckoutWithSubtotal } from '@/views/PayItForward/TheatricalPayItForwardView/components/TheatricalPifExpressOffers/TheatricalPifCheckoutWithSubtotal'
import { usePifOverlay } from '../contexts/PifOverlayContext'
import { DavidGoalProgress } from './DavidGoalProgress'
import { DavidPriceOptions } from './DavidpriceOptions'

interface PifOverlayProps {
  releaseDate?: string
  projectSlug: string
  useDavidPifPanelV2?: boolean
}

export function PifOverlay({ releaseDate, projectSlug, useDavidPifPanelV2 = false }: PifOverlayProps) {
  const { t } = useTranslate('pif')
  const { isOpen, closeOverlay } = usePifOverlay()
  const { offersData, selectedOffer, handleSetSelectedOffer } = usePayItForwardOfferData()

  // Calculate tickets and credits eligibility based on selected offer
  const ticketsProvided = selectedOffer
    ? Math.floor(getPriceForCurrency(selectedOffer.price, selectedOffer.currency) / 15)
    : 0

  // Filter offers to only show those <= $7500
  const filteredOffers = useMemo(() => {
    return offersData?.filter((offer) => getPriceForCurrency(offer.price, offer.currency) <= 7500) || []
  }, [offersData])

  // Prevent body scroll when overlay is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  if (!selectedOffer) return null

  const selectedPrice = formatPrice(selectedOffer.price, {
    currency: selectedOffer.currency,
    ignoreEmptyDecimal: true,
  })

  const offerPath = `${paths.payItForward.index}/${projectSlug}/checkout/${selectedOffer.id}`

  if (useDavidPifPanelV2) {
    return (
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop - only visible on desktop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="fixed top-0 z-40 hidden min-h-svh w-screen bg-black/30 md:block"
              onClick={closeOverlay}
            />

            {/* Overlay Panel */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{
                type: 'spring',
                damping: 30,
                stiffness: 300,
              }}
              className="fixed inset-y-0 right-0 z-50 flex w-full flex-col bg-[#FCF4ED] md:max-w-[480px]"
            >
              <div className="flex w-full flex-col gap-6 overflow-y-auto p-6 !pb-[200px] md:p-10">
                <button onClick={closeOverlay} className="mr-auto shrink-0">
                  <CloseIcon size={32} color="black" />
                </button>
                <div className="flex flex-col gap-2">
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-angel-royal relative shrink-0 text-[2.5rem] lowercase not-italic leading-[2rem] text-[#411E09] md:text-5xl md:leading-[2.5rem]">
                      {t('payItForward', 'Pay it Forward')}
                    </p>
                    <p className="font-angel-royal relative text-[2.5rem] lowercase not-italic leading-[2rem] text-[#411E09] md:text-5xl md:leading-[2.5rem]">
                      {selectedPrice}
                    </p>
                  </div>

                  <Text className="photon-paragraph-sm md:photon-paragraph-md !text-[#411E09]">
                    {t(
                      'davidPifOverlayDescription',
                      'Give a child the chance to see DAVID on the big screen—for free.',
                    )}
                  </Text>
                </div>
                <DavidGoalProgress
                  releaseDate={releaseDate}
                  textColor="#411e09"
                  progressBarBg="bg-white"
                  progressBarBorder="border-[#f0e0d3]"
                  progressBarFillBg="bg-[#ea6f13]"
                  textSize="sm"
                  shouldAnimateOnScroll={false}
                  showGoalLineTitle={false}
                />
                <DavidPriceOptions
                  offers={filteredOffers}
                  onClick={handleSetSelectedOffer}
                  selectedOffer={selectedOffer}
                  shouldShowPeopleImpacted={false}
                  shouldUseEqualColumns
                />
                <div className="flex flex-col gap-2">
                  <p className="font-angel-royal relative shrink-0 text-[1.75rem] lowercase not-italic leading-[1.5rem] text-[#411E09] md:text-[2rem]">
                    {t('yourImpact', 'Your Impact')}
                  </p>

                  <div className="flex items-center gap-1">
                    <CircleCheckIcon size={20} color1="david-orange" className="relative shrink-0 md:size-6" />

                    <Text className="photon-caption-sm md:photon-caption-md !text-[#411E09]" weight="bold">
                      {t('payItForward', 'Pay it Forward')}{' '}
                      <span className="!text-medium italic">
                        (
                        {t('ticketsCount', {
                          count: ticketsProvided,
                          ticketsProvided,
                          defaultValue_one: '{{ticketsProvided}} Ticket',
                          defaultValue_other: '{{ticketsProvided}} Tickets',
                        })}
                        )
                      </span>
                    </Text>
                  </div>
                </div>
              </div>

              <div className="fixed inset-x-0 bottom-0 flex w-full shrink-0 border-t border-[#E4D8CD] bg-[#FCF4ED] px-6 py-4 md:border-transparent md:!p-10">
                <LinkButton
                  href={offerPath}
                  variant="plain"
                  size="md"
                  className={classNames(
                    'flex w-full !py-[.875rem] !rounded-xl !px-[1.25rem] items-center justify-between gap-6 bg-david-orange border-david-orange text-white hover:border-core-warning-700 hover:bg-core-warning-700 active:border-core-warning-700 active:bg-core-warning-700',
                  )}
                >
                  <span className="font-angel-royal flex items-center gap-1.5 text-[1.625rem] !lowercase not-italic leading-[1.5rem] text-white">
                    <PayItForwardOutlineIcon className="shrink-0" color="white" size={24} />
                    {t('payItForward', 'Pay it Forward')}
                  </span>
                  <span className="font-angel-royal flex items-center text-[1.625rem] !lowercase not-italic leading-[1.5rem] text-white">
                    {selectedPrice}
                    <CaretRightIcon color="white" size={24} />
                  </span>
                </LinkButton>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    )
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop - only visible on desktop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed top-0 z-40 hidden min-h-svh w-screen bg-black/30 md:block"
            onClick={closeOverlay}
          />

          {/* Overlay Panel */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{
              type: 'spring',
              damping: 30,
              stiffness: 300,
            }}
            className="fixed inset-y-0 right-0 z-50 w-screen md:h-full md:w-full md:max-w-[480px]"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Unified Layout */}
            <div className="relative flex h-full w-full flex-col bg-[#fcf4ed] md:h-full md:overflow-y-auto">
              {/* Close Button - Mobile: Fixed Header, Desktop: Inside Content */}
              <div className="h relative w-full shrink-0 px-6 pb-3 pt-6 md:hidden">
                <button
                  onClick={closeOverlay}
                  className="relative block size-8 shrink-0 cursor-pointer p-1"
                  aria-label="Close"
                >
                  <svg
                    className="block size-full"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Scrollable Content - Mobile only, Desktop scrolls entire container */}
              <div className="h-full flex-1 overflow-y-auto md:flex-none">
                <div className="relative flex h-full flex-col gap-6 px-6 py-4 md:p-10">
                  {/* Desktop Close Button */}
                  <div className="hidden w-full shrink-0 md:block">
                    <button
                      onClick={closeOverlay}
                      className="relative block size-8 shrink-0 cursor-pointer p-1"
                      aria-label="Close"
                    >
                      <svg
                        className="block size-full"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  {/* Content Wrapper - grows on desktop */}
                  <div className="relative flex flex-col gap-4 md:min-h-0 md:grow md:gap-6">
                    {/* Header */}
                    <div className="font-angel-royal relative flex w-full shrink-0 items-start justify-between whitespace-pre text-nowrap text-[40px] lowercase leading-[32px] md:text-[48px] md:leading-[40px]">
                      <p className="relative shrink-0">{t('payItForward', 'Pay it Forward')}</p>
                      <p className="relative shrink-0">{selectedPrice}</p>
                    </div>

                    {/* Description */}
                    <p className="font-whitney-medium relative w-full shrink-0 text-[14px] not-italic leading-[26px] text-[#411e09] md:text-[16px] md:leading-[28px]">
                      {t(
                        'davidPifOverlayDescription',
                        'Give a child the chance to see DAVID on the big screen—for free.',
                      )}
                    </p>

                    {/* Progress */}
                    <DavidGoalProgress
                      releaseDate={releaseDate}
                      textColor="#411e09"
                      progressBarBg="bg-white"
                      progressBarBorder="border-[#f0e0d3]"
                      progressBarFillBg="bg-[#ea6f13]"
                      textSize="sm"
                      shouldAnimateOnScroll={false}
                      showGoalLineTitle={false}
                    />

                    {/* Price Options */}
                    <div className="relative w-full shrink-0">
                      <TheatricalExpressPriceOptions
                        offers={filteredOffers}
                        onClick={handleSetSelectedOffer}
                        selectedOffer={selectedOffer}
                        shouldShowPeopleImpacted={false}
                        shouldUseEqualColumns
                        newPifLayout
                        pifNameInCreditsEnabled={false}
                      />
                    </div>

                    {/* Impact */}
                    <div className="relative flex w-full shrink-0 flex-col content-stretch items-start gap-2">
                      <p className="font-angel-royal relative w-full shrink-0 text-[32px] lowercase not-italic leading-[24px] text-[#411e09]">
                        {t('yourImpact', 'Your Impact')}
                      </p>
                      <div className="relative flex w-full shrink-0 flex-col content-stretch items-start gap-2">
                        {/* Tickets Impact */}
                        <div className="relative flex w-full shrink-0 content-stretch items-center gap-1">
                          <CircleCheckIcon size={24} color1="david-orange" className="relative shrink-0" />
                          <CaptionMD>
                            {t('payItForward', 'Pay it Forward')}{' '}
                            <i>
                              (
                              {t('ticketsCount', {
                                count: ticketsProvided,
                                ticketsProvided,
                                defaultValue_one: '{{ticketsProvided}} Ticket',
                                defaultValue_other: '{{ticketsProvided}} Tickets',
                              })}
                              )
                            </i>
                          </CaptionMD>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Checkout Button - positioned at bottom on desktop */}
                  <div className="hidden md:block">
                    <TheatricalPifCheckoutWithSubtotal
                      projectSlug={projectSlug}
                      selectedOffer={selectedOffer}
                      isSubscription={false}
                      newPifLayout
                      pifNameInCreditsEnabled={false}
                      overlayStyle
                    />
                  </div>
                </div>
              </div>

              {/* Fixed Bottom Button - Mobile only */}
              <div className="w-full shrink-0 border-t border-[#e4d8cd] bg-[#fcf4ed] p-4 md:mt-auto md:hidden">
                <TheatricalPifCheckoutWithSubtotal
                  projectSlug={projectSlug}
                  selectedOffer={selectedOffer}
                  isSubscription={false}
                  newPifLayout
                  pifNameInCreditsEnabled
                  overlayStyle
                />
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
